import React, { useState } from "react";
import { BUSINESS } from "../lib/assets";
import { api } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

export default function Contact() {
  usePageMeta({
    title: "Contact Us",
    description: "Get in touch with When Pigs Fly Family BBQ in Spring Hill, FL. Call, email, or send a message for catering, wholesale, or general questions.",
    path: "/contact",
  });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try again or call us directly.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="border-b border-ink py-16 md:py-24" data-testid="contact-hero">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="eyebrow mb-3 text-ink/70">Say hi</div>
          <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">Contact Us</h1>
          <p className="mt-6 max-w-2xl font-serif text-base leading-relaxed text-ink/80">
            Questions, quotes, or just want to swap brisket stories? Drop a line — we read every message.
          </p>
        </div>
      </section>

      <section className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-5">
            <SectionHeading eyebrow="How to reach us" title="The Direct Line" />
            <ul className="mt-10 space-y-6 font-serif text-base">
              <li className="flex items-start gap-4">
                <MapPin size={18} className="mt-1 shrink-0" />
                <div>
                  <div className="eyebrow mb-1 text-ink/60">Address</div>
                  <p>{BUSINESS.street}<br />{BUSINESS.city}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={18} className="mt-1 shrink-0" />
                <div>
                  <div className="eyebrow mb-1 text-ink/60">Phone</div>
                  <p><a href={`tel:${BUSINESS.phoneRaw}`} className="link-underline" data-testid="contact-phone-link">{BUSINESS.phone}</a></p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={18} className="mt-1 shrink-0" />
                <div>
                  <div className="eyebrow mb-1 text-ink/60">Email</div>
                  <a
                    href={`mailto:${BUSINESS.email}`}
                    data-testid="contact-email-link"
                    className="link-underline break-all font-serif text-base text-ink"
                  >
                    {BUSINESS.email}
                  </a>
                </div>
              </li>
            </ul>
            <div className="mt-10 border border-ink p-6">
              <div className="eyebrow mb-2 text-ink/60">Hours</div>
              <table className="w-full font-mono text-xs">
                <tbody>
                  {BUSINESS.hours.map(([d, h]) => (
                    <tr key={d} className="border-b border-ink/20 last:border-b-0">
                      <td className="py-2 uppercase text-ink/70">{d}</td>
                      <td className="py-2 text-right">{h}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-7 border border-ink bg-bone p-6 shadow-hard md:p-10" data-testid="contact-form">
            <div className="eyebrow mb-6 text-ink/60">Send a message</div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2">
              <Field label="Name *" required value={form.name} onChange={set("name")} testid="contact-name" />
              <Field label="Email *" type="email" required value={form.email} onChange={set("email")} testid="contact-email" />
              <Field label="Phone" value={form.phone} onChange={set("phone")} testid="contact-phone" />
              <Field label="Subject" value={form.subject} onChange={set("subject")} testid="contact-subject" />
              <div className="md:col-span-2">
                <label className="eyebrow mb-2 block text-ink/70">Message *</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message"
                  className="w-full border border-ink bg-transparent px-3 py-3 font-serif text-sm focus:outline-none"
                />
              </div>
            </div>
            <div className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-ink pt-6 md:flex-row md:items-center">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">We reply within 24 hours.</p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={14} /> : null} Send Message
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="eyebrow mb-2 block text-ink/70">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full border border-ink bg-transparent px-3 py-3 font-serif text-sm focus:outline-none"
      />
    </div>
  );
}
